/**
 * getSelectableFriends
 * Pure helper: filter friends to accepted-only, optionally exclude IDs, optional search by name/email.
 * Used by FriendSelectorList (and optionally Create flow). Callers should use useMemo with
 * [friends, excludeIds, searchQuery] so the filter only runs when inputs change (e.g. searchQuery per keystroke).
 */

import type { Friend } from '../types/friends.types'
import { getSafeDisplayName } from '../../create/helpers/createUi.helper'

const ACCEPTED = 'accepted'

export interface GetSelectableFriendsOptions {
  /** IDs to exclude (e.g. already in group). */
  excludeIds?: string[]
  /** Optional search query; filters by name or email (case-insensitive). */
  searchQuery?: string
}

/**
 * Returns accepted friends, excluding given IDs, optionally filtered by search query.
 */
export function getSelectableFriends(
  friends: Friend[],
  options: GetSelectableFriendsOptions = {}
): Friend[] {
  const { excludeIds = [], searchQuery = '' } = options
  const excludeSet = new Set(excludeIds)

  const accepted = friends.filter(
    (f) => (f.friendshipStatus ?? ACCEPTED) === ACCEPTED && !excludeSet.has(f.id)
  )

  if (!searchQuery.trim()) return accepted

  const q = searchQuery.trim().toLowerCase()
  return accepted.filter((f) => {
    const name = getSafeDisplayName(f.name, f.email).toLowerCase()
    const email = (f.email ?? '').toLowerCase()
    return name.includes(q) || email.includes(q)
  })
}
